import React from 'react';
import {Box, Chip, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {useTranslation} from 'react-i18next';
import {Accordion} from '@/components/ui/accordion';
import {formatCurrency, formatDistance, formatDuration} from '@/utils/formatters';
import {useIsMobile} from '@/hooks/use-mobile';
import TripItem, {TripData} from './TripItem';

export interface DriverData {
  driverNumber: number;
  name: string;
  gender?: string | null;
  isSystemDriver: boolean;
}

export interface ShiftData {
  uuid: string;
  sequenceNumber: number;
  startAt: string;
  endAt: string;
  duration: number;
  tripsCount: number;
  mileageStart: number;
  mileageEnd: number;
  forHireDistance: number;
  hiredDistance: number;
  totalDistance: number;
  totalAmount: number;
  subtractedTotalAmount: number;
  finalTotalAmount: number;
  extrasAmount: number;
  subtractedExtrasAmount: number;
  finalExtrasAmount: number;
  licenseNumber: string;
  driverNumber: number;
  driver: DriverData;
  trips: TripData[];
}

export interface ShiftInstallationData {
  customerNumber: string;
  customerBusinessName: string;
  deviceManufacturerNumber: number;
  deviceType: string;
  id: string;
  installationUuid: string;
}

export type ExtendedShiftData = ShiftData & ShiftInstallationData;

interface ShiftItemProps {
  shift: ShiftData;
}

const ShiftItem: React.FC<ShiftItemProps> = ({shift}) => {
  const {t} = useTranslation();
  const isMobile = useIsMobile();

  return (

    <Box sx={{p: 1}}>
      <Grid container spacing={2}>
        <Grid size={{xs: 6, md: 3}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.driver')}
          </Typography>
          <Typography variant="body2">
            {shift.driver?.name || t('assignments.unknownDriver')}
            {shift.driver?.isSystemDriver && (
              <Chip
                label={t('assignments.systemDriver')}
                color="info"
                size="small"
                sx={{ml: 1, height: 20, fontSize: '0.7rem'}}
              />
            )}
          </Typography>
          {shift.driver?.gender && (
            <Typography variant="caption" color="text.secondary">
              {t(`assignments.gender.${shift.driver.gender}`)}
            </Typography>
          )}
        </Grid>
        <Grid size={{xs: 6, md: 3}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.driverNumber')}
          </Typography>
          <Typography variant="body2">
            {shift.driver?.driverNumber || '-'}
          </Typography>
        </Grid>
        <Grid size={{xs: 6, md: 3}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.licenseNumber')}
          </Typography>
          <Typography variant="body2">
            {shift.licenseNumber}
          </Typography>
        </Grid>
        <Grid size={{xs: 6, md: 3}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.duration')}
          </Typography>
          <Typography variant="body2">
            {formatDuration(shift.duration)}
          </Typography>
        </Grid>
        <Grid size={{xs: 6, md: 3}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.totalDistance')}
          </Typography>
          <Typography variant="body2">
            {formatDistance(shift.totalDistance)}
          </Typography>
        </Grid>
        <Grid size={{xs: 6, md: 3}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.mileageStart')}
          </Typography>
          <Typography variant="body2">
            {formatDistance(shift.mileageStart)}
          </Typography>
        </Grid>
        <Grid size={{xs: 6, md: 3}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.mileageEnd')}
          </Typography>
          <Typography variant="body2">
            {formatDistance(shift.mileageEnd)}
          </Typography>
        </Grid>
        <Grid size={{xs: 6, md: 3}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.hiredDistance')}
          </Typography>
          <Typography variant="body2">
            {formatDistance(shift.hiredDistance)}
          </Typography>
        </Grid>
        <Grid size={{xs: 6, md: 3}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.forHireDistance')}
          </Typography>
          <Typography variant="body2">
            {formatDistance(shift.forHireDistance)}
          </Typography>
        </Grid>
      </Grid>

      <Box sx={{mt: 3, mb: 1}}>
        <Typography variant="subtitle1">
          {t('assignments.financialSummary')}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{xs: 6, md: 4}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.totalAmount')}
          </Typography>
          <Typography variant="body2">
            {formatCurrency(shift.totalAmount)}
          </Typography>
        </Grid>
        <Grid size={{xs: 6, md: 4}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.subtractedAmount')}
          </Typography>
          <Typography variant="body2">
            {formatCurrency(shift.subtractedTotalAmount)}
          </Typography>
        </Grid>
        <Grid size={{xs: 6, md: 4}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.finalAmount')}
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {formatCurrency(shift.finalTotalAmount)}
          </Typography>
        </Grid>
        <Grid size={{xs: 6, md: 4}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.extrasAmount')}
          </Typography>
          <Typography variant="body2">
            {formatCurrency(shift.extrasAmount)}
          </Typography>
        </Grid>
        <Grid size={{xs: 6, md: 4}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.subtractedExtras')}
          </Typography>
          <Typography variant="body2">
            {formatCurrency(shift.subtractedExtrasAmount)}
          </Typography>
        </Grid>
        <Grid size={{xs: 6, md: 4}}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.finalExtras')}
          </Typography>
          <Typography variant="body2">
            {formatCurrency(shift.finalExtrasAmount)}
          </Typography>
        </Grid>
      </Grid>

      {shift.trips && shift.trips.length > 0 && (
        <Box sx={{mt: 3}}>
          <Typography variant="subtitle1" gutterBottom>
            {t('assignments.trips')} ({shift.trips.length})
          </Typography>

          <Accordion type="multiple" className="w-full">
            {shift.trips.map((trip) => (
              <TripItem key={trip.uuid} trip={trip}/>
            ))}
          </Accordion>
        </Box>
      )}
    </Box>
  );
};

export default ShiftItem;
